import axios from 'axios'
import { defineStore } from 'pinia'
import { BASE_URL } from '@/environment/environment'
import { getApiError } from '@/composables/getApiError'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useFilesStore } from '@/modules/files/stores/filesStore'
import type { ILinksSettingsStoreState, IUsefulLinkPayload } from '@/modules/links/types/ILinksSettings'
import type { IUsefulLink } from '@/modules/links/types/IUsefulLink'

const LINKS_URL = `${BASE_URL}/api/links/useful-link/`

/**
 * Стор администрирования полезных ссылок (право `links.edit`).
 *
 * Ссылки с `is_daomed_main` приходят с мейн-портала: бэк разрешает менять у них
 * только `is_active` и `is_pinned` и запрещает удаление.
 * После каждой мутации перезагружается таблица админки; пользовательский список
 * `linksStore` обновляется сам при заходе на `/links`.
 */
export const useLinksSettingsStore = defineStore('linksSettingsStore', {
  state: (): ILinksSettingsStoreState => ({
    links: [],
    nextCursor: null,
    filters: {
      search: '',
    },
  }),

  actions: {
    /**
     * Загружает страницу ссылок для таблицы админки
     *
     * @param cursor Курсор следующей страницы; без него список загружается заново
     */
    async fetchLinks(cursor?: string): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const params: Record<string, string> = {}
        if (this.filters.search) params.search = this.filters.search
        if (cursor) params.cursor = cursor

        const response = await axios.get(LINKS_URL, { params })
        const results: IUsefulLink[] = response.data.results ?? response.data
        this.links = cursor ? [...this.links, ...results] : results
        this.nextCursor = response.data.next ?? null
      } catch (error) {
        console.error('❌ Ошибка загрузки полезных ссылок:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить полезные ссылки'),
          time: 5000,
        })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },
    async createLink(payload: IUsefulLinkPayload): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        await axios.post(LINKS_URL, useFilesStore().toFormData(payload))
        feedbackStore.showToast({
          type: 'success',
          message: 'Ссылка успешно создана',
          time: 3000,
        })
        await this.fetchLinks()
        return true
      } catch (error) {
        console.error('❌ Ошибка создания полезной ссылки:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось создать ссылку'),
          time: 5000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async updateLink(id: string, payload: Partial<IUsefulLinkPayload>): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        /* multipart не передаёт null, поэтому удаление логотипа уходит JSON-ом (`logo: null`) */
        const body = payload.logo === null ? payload : useFilesStore().toFormData(payload)
        await axios.patch(`${LINKS_URL}${id}/`, body)
        feedbackStore.showToast({
          type: 'success',
          message: 'Ссылка успешно обновлена',
          time: 3000,
        })
        await this.fetchLinks()
        return true
      } catch (error) {
        console.error(`❌ Ошибка обновления полезной ссылки ${id}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось сохранить изменения ссылки'),
          time: 5000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async toggleLinkActive(id: string, isActive: boolean): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        await axios.patch(`${LINKS_URL}${id}/`, { is_active: isActive })
        feedbackStore.showToast({
          type: 'success',
          message: isActive ? 'Ссылка активирована' : 'Ссылка деактивирована',
          time: 3000,
        })
        await this.fetchLinks()
        return true
      } catch (error) {
        console.error(`❌ Ошибка переключения активности ссылки ${id}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось изменить активность ссылки'),
          time: 5000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async toggleLinkPin(id: string): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post<{ is_pinned: boolean }>(`${LINKS_URL}${id}/pin/`)
        feedbackStore.showToast({
          type: 'success',
          message: response.data.is_pinned ? 'Ссылка закреплена' : 'Ссылка откреплена',
          time: 3000,
        })
        await this.fetchLinks()
        return true
      } catch (error) {
        console.error(`❌ Ошибка закрепления ссылки ${id}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось изменить закрепление ссылки'),
          time: 5000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async deleteLink(id: string): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        await axios.delete(`${LINKS_URL}${id}/`)
        feedbackStore.showToast({
          type: 'success',
          message: 'Ссылка успешно удалена',
          time: 3000,
        })
        await this.fetchLinks()
        return true
      } catch (error) {
        console.error(`❌ Ошибка удаления полезной ссылки ${id}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось удалить ссылку'),
          time: 5000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },
  },
})
